/**
 * Curated permit-portal directory for Apex's jurisdictions.
 *
 * Every URL was live-verified 2026-07-06 (curl 200, or documented bot-shield:
 * several CivicPlus city sites return 403 to non-browser agents but load fine
 * in a real browser - those entries link the official page confirmed via the
 * city's own search listings). Do not add guessed URLs; verify before adding.
 */

export interface PermitPortal {
  jurisdiction: string;
  portalUrl: string;
  platform: string;
  notes?: string;
}

export const PERMIT_PORTALS: PermitPortal[] = [
  // ── Counties ──
  {
    jurisdiction: 'Riverside County',
    portalUrl: 'https://rctlma.org/plus-online',
    platform: 'PLUS Online',
    notes: 'Unincorporated county + TLMA land-use cases',
  },
  {
    jurisdiction: 'San Bernardino County',
    portalUrl: 'https://aca-prod.accela.com/SBC/Default.aspx',
    platform: 'Accela (EZOP)',
    notes: 'Unincorporated county; EZOP home at wp.sbcounty.gov/ezop',
  },

  // ── Inland Empire cities ──
  {
    jurisdiction: 'Ontario',
    portalUrl: 'https://aca-prod.accela.com/ONT/Cap/CapHome.aspx?module=Permits&TabName=HOME',
    platform: 'Accela',
    notes: 'No automated feed - check permits here (deep-links to permit search)',
  },
  {
    jurisdiction: 'Rancho Cucamonga',
    portalUrl: 'https://aca-prod.accela.com/cityofrc/default.aspx',
    platform: 'Accela',
  },
  {
    jurisdiction: 'Fontana',
    portalUrl: 'https://aca-prod.accela.com/FONTANA/Default.aspx',
    platform: 'Accela',
  },
  {
    jurisdiction: 'Riverside (city)',
    portalUrl: 'https://riversideca.gov/cedd/planning/public-permit-portal',
    platform: 'POSSE LMS',
    notes: 'Entry page; portal runs at posselms.riversideca.gov',
  },
  {
    jurisdiction: 'San Bernardino (city)',
    portalUrl: 'https://www.sanbernardino.gov/801/Online-Permits',
    platform: 'Citizen Access',
    notes: 'Entry page; the portal subdomain rejects direct links',
  },
  {
    jurisdiction: 'Moreno Valley',
    portalUrl: 'https://aca-prod.accela.com/MOVAL/Default.aspx',
    platform: 'Accela (SimpliCITY)',
  },
  {
    jurisdiction: 'Perris',
    portalUrl: 'https://aca-prod.accela.com/perris/default.aspx',
    platform: 'Accela',
  },
  {
    jurisdiction: 'Corona',
    portalUrl: 'https://etrakit.coronaca.gov/etrakit/',
    platform: 'eTRAKiT',
    notes: 'Public permit search works without a login',
  },
  {
    jurisdiction: 'Beaumont',
    portalUrl: 'https://beaumontca.gov/1295/Apply-for-PermitPlan-Review',
    platform: 'Email + CSS',
    notes: 'Submittal via permits@beaumontca.gov; status via CSS portal linked on this page',
  },
  {
    jurisdiction: 'Redlands',
    portalUrl: 'https://gis.cityofredlands.org/pol',
    platform: 'City portal',
    notes: 'Over-the-counter permit types only',
  },

  // ── Coachella Valley cities ──
  {
    jurisdiction: 'Palm Desert',
    portalUrl: 'https://palmdesert.my.site.com/s/',
    platform: 'Clariti',
  },
  {
    jurisdiction: 'Indio',
    portalUrl: 'https://www.indio.org/services/citizen-self-service',
    platform: 'Tyler CSS',
    notes: 'All permit applications online-only since Jan 2024',
  },
  {
    jurisdiction: 'La Quinta',
    portalUrl: 'https://www.laquintaca.gov/business/the-hub/the-hub-online-portal',
    platform: 'Tyler (The HUB)',
  },
  {
    jurisdiction: 'Palm Springs',
    portalUrl: 'https://www.palmspringsca.gov/government/departments/building/permits',
    platform: 'Palm Springs Online',
    notes: 'Paper applications no longer accepted',
  },
  {
    jurisdiction: 'Cathedral City',
    portalUrl: 'https://www.cathedralcity.gov/business/self-service-portal-help',
    platform: 'Citizen Self Service',
  },
  {
    jurisdiction: 'Coachella',
    portalUrl: 'https://www.coachella.org/departments/development-services/building-division',
    platform: 'Email-based',
    notes: 'No public portal - applications go to buildingservices@coachella.org',
  },
];
